import { Divider } from '../../Divider/Divider'
import React, { useCallback, useMemo, useState } from 'react'
import { Button } from '../../Button/Button'
import { WidgetProps } from '@rjsf/utils'
import './DiscussionTopicsWidget.scss'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'

interface Topic {
  topicName: string
  topicDescription: string
}

const _PRE_BUILT_TOPICS: Topic[] = [
  {
    topicName: 'Market Impacts',
    topicDescription: "Discussion about how economic conditions, market movements, or financial events affect the client's portfolio or financial situation.",
  },
  {
    topicName: 'Legislative Changes',
    topicDescription: "Conversation about new or proposed laws and regulations that may impact the client's finances.",
  },
  {
    topicName: 'Elections',
    topicDescription: 'Discussion about how election outcomes or political changes could affect financial planning or investments.',
  },
  {
    topicName: 'Business Ownership',
    topicDescription: 'Conversation related to the client owning, operating, selling, or transitioning a business.',
  },
  {
    topicName: 'Financial Goals',
    topicDescription: "Discussion about the client's short-term or long-term financial objectives and priorities",
  },
  {
    topicName: 'Family Legacy',
    topicDescription: 'Conversation about values, wishes for future generations, or the non-financial aspects of what the client wants to leave behind.',
  },
  {
    topicName: 'Estate Planning',
    topicDescription: 'Discussion about wills, trusts, beneficiary designations, or legal strategies for transferring wealth after death.',
  },
  {
    topicName: 'Diversification',
    topicDescription: 'Conversation about spreading investments across different assets or strategies to manage risk.',
  },
  {
    topicName: 'Tax Planning',
    topicDescription: 'Discussion about strategies to manage tax liabilities or optimize tax outcomes.',
  },
  {
    topicName: 'Risk Management',
    topicDescription: 'Conversation about protecting against financial losses, including insurance or other protective strategies.',
  },
  {
    topicName: 'Long-Term Care',
    topicDescription: 'Discussion about planning for or financing extended healthcare needs in later life.',
  },
  {
    topicName: 'Social Security',
    topicDescription: 'Conversation about Social Security benefits, claiming strategies, or timing decisions.',
  },
  {
    topicName: 'Competitor Services',
    topicDescription: 'Mention of services, products, or advice offered by other financial advisors or institutions.',
  },
  {
    topicName: 'Life Insurance',
    topicDescription: 'Discussion about life insurance policies, coverage needs, or using life insurance in financial planning.',
  },
  {
    topicName: 'Compliance',
    topicDescription: "Discussion about regulatory requirements, disclosure obligations, or compliance documentation related to the client's accounts.",
  },
  {
    topicName: 'Personal',
    topicDescription: 'Conversation about personal life, family updates, hobbies, health, travel, or other non-financial topics beyond simple greetings.',
  },
  {
    topicName: 'Real Estate',
    topicDescription:
      'Conversation about property the client owns or is buying or selling, including a primary residence, rental properties, vacation homes, or land.',
  },
  {
    topicName: 'Medicare',
    topicDescription: 'Discussion about Medicare eligibility, enrollment timing, plan parts or supplements, or premium costs.',
  },
  {
    topicName: 'Beneficiaries',
    topicDescription: "Conversation about who is named as a beneficiary on the client's accounts or policies, or about updating those designations.",
  },
  {
    topicName: 'Health & Medical',
    topicDescription: "Discussion about the client's health conditions, medical expenses, or disability, and how these affect their financial situation.",
  },
  {
    topicName: 'Charitable Giving',
    topicDescription: "Conversation about donations, charitable vehicles such as donor-advised funds, or the client's philanthropic intentions.",
  },
  {
    topicName: 'Referrals',
    topicDescription:
      'Conversation about a person the client could introduce to the advisor, such as a friend, colleague, or family member whose situation suggests they may need financial advice.',
  },
  {
    topicName: 'Roth Conversion',
    topicDescription: 'Discussion about converting traditional retirement assets to a Roth account, including the timing and the tax consequences.',
  },
  {
    topicName: 'Employment Change',
    topicDescription: "Conversation about a new job, promotion, layoff, retirement date, or a change in the client's compensation or benefits.",
  },
  {
    topicName: 'Cash Management',
    topicDescription: "Discussion about the client's cash reserves, emergency savings, or how their income and spending are handled.",
  },
  {
    topicName: 'Debt & Mortgage',
    topicDescription: 'Conversation about mortgages, refinancing, loans, credit balances, or a strategy for paying down debt.',
  },
  {
    topicName: 'Required Minimum Distributions',
    topicDescription: 'Discussion about required withdrawals from retirement accounts, including timing, amounts, or penalties for missing them.',
  },
  {
    topicName: 'Fees',
    topicDescription: 'Conversation about advisory fees, fund expenses, commissions, or what the client pays for the services they receive.',
  },
  {
    topicName: 'Business Succession',
    topicDescription: 'Discussion about how the client will exit, sell, or hand over their business, including successor plans and valuation.',
  },
  {
    topicName: 'Education Funding',
    topicDescription: "Conversation about paying for a child's or grandchild's education, including savings plans and expected costs.",
  },
  {
    topicName: 'Investment Products',
    topicDescription: 'Discussion about specific investment vehicles, funds, or financial instruments being considered or held.',
  },
]

const _MAX_CUSTOM_TOPICS = 5
const _MAX_TOPIC_NAME_LENGTH = 25
const _MIN_TOPIC_DESCRIPTION_LENGTH = 50
const _MAX_TOPIC_DESCRIPTION_LENGTH = 100

export const DiscussionTopicsWidget: React.FC<WidgetProps> = ({ value, disabled = false, onChange }) => {
  const topics = value as Topic[]
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const handleDone = useCallback(
    (index: number, updatedTopic: Topic) => {
      const topicsCopy = [...topics]
      topicsCopy[index] = updatedTopic
      onChange(topicsCopy)
      setEditingIndex(null)
    },
    [onChange, topics],
  )

  const handleDelete = useCallback(
    (index: number) => {
      const topicsCopy = [...topics]
      topicsCopy.splice(index, 1)
      onChange(topicsCopy)
      setEditingIndex(null)
    },
    [onChange, topics],
  )

  const handleAdd = useCallback(() => {
    const topicsCopy = [...topics]
    topicsCopy.push({ topicName: '', topicDescription: '' })
    onChange(topicsCopy)
    setEditingIndex(topicsCopy.length - 1)
  }, [onChange, topics])

  return (
    <div className='d-flex flex-column zocks-gap-2 pt-2'>
      <div className='d-flex flex-column gap-3'>
        <div className='d-flex flex-column'>
          <Divider />
          {topics.map((topic: Topic, index: number) => (
            <React.Fragment key={index}>
              <CustomTopicRow
                topic={topic}
                disabled={disabled || editingIndex !== null}
                isEditing={editingIndex === index}
                onEdit={() => setEditingIndex(index)}
                onDone={(updatedTopic) => handleDone(index, updatedTopic)}
                onCancel={() => setEditingIndex(null)}
                onDelete={() => handleDelete(index)}
              />
              <Divider />
            </React.Fragment>
          ))}
        </div>
        {topics.length < _MAX_CUSTOM_TOPICS ? (
          <Button
            variant='primaryLinkButton'
            onClick={handleAdd}
            disabled={disabled || editingIndex !== null}
            className='p-0 align-self-start button-medium border-0 text-decoration-none'
          >
            + Add item
          </Button>
        ) : null}
      </div>
      <PreBuiltTopics preBuiltTopics={_PRE_BUILT_TOPICS} />
    </div>
  )
}

interface CustomTopicRowProps {
  topic: Topic
  disabled: boolean
  isEditing: boolean
  onEdit: () => void
  onDone: (topic: Topic) => void
  onCancel: () => void
  onDelete: () => void
}

const CustomTopicRow: React.FC<CustomTopicRowProps> = ({ topic, disabled, isEditing, onEdit, onDone, onCancel, onDelete }) => {
  const [topicName, setTopicName] = useState<string>(topic.topicName)
  const [topicDescription, setTopicDescription] = useState<string>(topic.topicDescription)

  const handleDone = useCallback(() => {
    onDone({ topicName, topicDescription })
  }, [onDone, topicName, topicDescription])

  const handleCancel = useCallback(() => {
    if (topic.topicName === '') {
      onDelete()
    } else {
      setTopicName(topic.topicName)
      setTopicDescription(topic.topicDescription)
      onCancel()
    }
  }, [topic, onDelete, onCancel])

  const remainingNameCharacters = _MAX_TOPIC_NAME_LENGTH - topicName.length
  const nameIsAtLimit = remainingNameCharacters === 0
  const nameIsNearLimit = remainingNameCharacters < 5 && remainingNameCharacters > 0

  const remainingDescriptionCharacters = _MAX_TOPIC_DESCRIPTION_LENGTH - topicDescription.length
  const descriptionIsAtLimit = remainingDescriptionCharacters === 0
  const descriptionIsNearLimit = remainingDescriptionCharacters < 20 && remainingDescriptionCharacters > 0
  const descriptionIsTooShort = topicDescription.length < _MIN_TOPIC_DESCRIPTION_LENGTH && topicDescription.length > 0

  const isNameValid = topicName.length > 0 && topicName.length <= _MAX_TOPIC_NAME_LENGTH
  const isDescriptionValid =
    topicDescription.length === 0 || (topicDescription.length >= _MIN_TOPIC_DESCRIPTION_LENGTH && topicDescription.length <= _MAX_TOPIC_DESCRIPTION_LENGTH)
  const isFormValid = isNameValid && isDescriptionValid

  if (isEditing) {
    return (
      <div className='p-4 bg-light w-100'>
        <div className='mb-3'>
          <div className='d-flex justify-content-between align-items-center mb-2'>
            <label className='form-label mb-0 fw-semibold'>Topic name</label>
            <div
              className={classNames('paragraph-extra-small', {
                'zocks-text-gray-600': !nameIsNearLimit && !nameIsAtLimit,
                'zocks-text-warning-text': nameIsNearLimit,
                'zocks-text-danger-200': nameIsAtLimit,
              })}
            >
              {nameIsAtLimit ? 'Limit reached' : `${remainingNameCharacters} characters left`}
            </div>
          </div>
          <Form.Control
            type='text'
            placeholder='e.g., Medicare Planning'
            value={topicName}
            onChange={(e) => setTopicName(e.target.value)}
            autoFocus
            maxLength={_MAX_TOPIC_NAME_LENGTH}
          />
          <small className='text-muted d-block mt-1'>Keep it short and specific. Used to group topics across meetings.</small>
        </div>

        <div className='mb-3'>
          <div className='d-flex justify-content-between align-items-center mb-2'>
            <div>
              <small className='form-label mb-0 fw-semibold'>Topic description</small>
              <small className='form-label mb-0 ms-1'> (optional)</small>
            </div>
            <div
              className={classNames('paragraph-extra-small', {
                'zocks-text-gray-600': !descriptionIsNearLimit && !descriptionIsAtLimit && !descriptionIsTooShort,
                'zocks-text-warning-text': descriptionIsNearLimit,
                'zocks-text-danger-200': descriptionIsAtLimit || descriptionIsTooShort,
              })}
            >
              {descriptionIsAtLimit
                ? 'Limit reached'
                : descriptionIsTooShort
                  ? `${_MIN_TOPIC_DESCRIPTION_LENGTH - topicDescription.length} more characters needed`
                  : `${remainingDescriptionCharacters} characters left`}
            </div>
          </div>
          <Form.Control
            type='text'
            placeholder='e.g., Medicare enrollment options, supplement plans, and cost planning strategies'
            value={topicDescription}
            onChange={(e) => setTopicDescription(e.target.value)}
            minLength={_MIN_TOPIC_DESCRIPTION_LENGTH}
            maxLength={_MAX_TOPIC_DESCRIPTION_LENGTH}
          />
          <small className='text-muted d-block mt-1'>Describe what to look for. The AI Assistant uses this to identify relevant discussions.</small>
        </div>

        <div className='d-flex gap-2'>
          <Button variant='primary' onClick={handleDone} disabled={!isFormValid}>
            Done
          </Button>
          <Button variant='secondary' onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className='d-flex justify-content-between align-items-center zocks-py-0.75'>
      <div className='flex-grow-1 align-items-center text-break'>
        <span>{topic.topicName}</span>
        {topic.topicDescription ? (
          <>
            <span>{' - '}</span>
            <span className='text-muted'>{topic.topicDescription}</span>
          </>
        ) : null}
      </div>
      <div className='d-flex gap-0'>
        <Button variant='borderless' onClick={onEdit} disabled={disabled} aria-label='Edit topic'>
          <i className='bi bi-pencil' />
        </Button>
        <Button variant='borderless' onClick={onDelete} disabled={disabled} aria-label='Delete topic'>
          <i className='bi bi-trash' />
        </Button>
      </div>
    </div>
  )
}

interface PreBuiltTopicProps {
  preBuiltTopics: Topic[]
}

const PreBuiltTopics: React.FC<PreBuiltTopicProps> = ({ preBuiltTopics }) => {
  const [showAllPrebuilt, setShowAllPrebuilt] = useState<boolean>(false)

  const topicsToDisplay = useMemo(() => (showAllPrebuilt ? preBuiltTopics : preBuiltTopics.slice(0, 3)), [showAllPrebuilt, preBuiltTopics])

  return (
    <div className='mb-4'>
      <div className='paragraph-medium bold pb-1'>Pre-built topics</div>
      <div className='text-muted paragraph-small pb-3'>Automatically identified in every meeting.</div>
      <Divider />
      <div className={`d-flex flex-column position-relative ${showAllPrebuilt ? 'expanded' : ''}`}>
        {!showAllPrebuilt && <div className='position-absolute bottom-0 start-0 end-0 discussion-topics-widget-gradient' />}
        {topicsToDisplay.map((topic) => (
          <React.Fragment key={topic.topicName}>
            <div className='zocks-py-0.75 flex-grow-1'>
              <span>{topic.topicName} -</span>
              <span className='text-muted ms-1'>{topic.topicDescription}</span>
            </div>
            <Divider />
          </React.Fragment>
        ))}
      </div>
      <div className='d-flex justify-content-center'>
        <Button variant='link' className='text-decoration-none p-0 zocks-text-primary button-link-medium' onClick={() => setShowAllPrebuilt((prev) => !prev)}>
          {!showAllPrebuilt ? `Show all ${preBuiltTopics.length} pre-built topics` : 'Show less'}
        </Button>
      </div>
    </div>
  )
}
